import type { IdentityApi, Session } from "../types";
import type { ActivityTracker } from "./activity";
import { createAccountApi } from "./account";
import { createAdminApi } from "./admin";
import { createAuthApi, type LiveCtx } from "./auth";
import { openLiveShareLink } from "../links/live";
import { createHttp, type Fetch } from "./http";
import { createInvitationLinkApi } from "./invitations";
import { createOrganisationApi } from "./organisation";
import { createSessionsApi } from "./sessions";
import type { SessionSignals } from "./signals";

/**
 * IdentityApi over the identity service (`crates/services/identity`; RFC 0001 §5–6), for VITE_IDENTITY_MODE=live.
 * The browser never talks to WorkOS and never holds a token: sign-in is AuthKit's hosted page, and the service keeps
 * an opaque HttpOnly cookie on this origin (D-026; FR-ADMIN-047).
 *
 * It serves all of IdentityApi: sign-in, the session, first run, organisation switching, the profile and preferences,
 * two-step verification, the organisation's details, members and invitations, invitation links, Request access, the
 * admin console (member roles, suspension, removal, policies, audit log), project access requests, sessions and
 * devices, and opening share links.
 */

export interface LiveIdentityOptions {
  /** Where the service's JSON API is on this origin: VITE_IDENTITY_API_PATH. */
  apiPath: string;
  fetch: Fetch;
  signals: SessionSignals;
  activity: ActivityTracker;
}

export function createLiveIdentity({
  apiPath,
  fetch,
  signals,
  activity,
}: LiveIdentityOptions): IdentityApi {
  let latest: Session | null | undefined;
  const ctx: LiveCtx = {
    http: createHttp(apiPath, fetch),
    signals,
    activity,
    remember(session) {
      latest = session;
      return session;
    },
    latest: () => latest,
  };

  const { http } = ctx;
  return {
    ...createAuthApi(ctx),
    ...createOrganisationApi(ctx),
    ...createAdminApi(ctx),
    ...createSessionsApi(ctx),
    ...createAccountApi(ctx),
    ...createInvitationLinkApi(ctx),
    subscribe: (listener) => signals.subscribe(listener),
    // Share links (23 §5.16–5.17): a visitor opens one without an account.
    openShareLink: (token) => openLiveShareLink(http, token),
    unlockShareLink: (token, password) => openLiveShareLink(http, token, password),
  };
}

export { createActivityTracker } from "./activity";
export { browserSignals, createSignals, SESSION_CHANNEL } from "./signals";
export type { SessionSignals } from "./signals";
export { IdentityServiceError } from "./errors";
